import { useEffect, useRef, type FormEvent } from 'react';
import { useInRouterContext, useLocation, useNavigate } from 'react-router-dom';

export interface ParsedProductRoute {
  readonly kind: 'list' | 'new' | 'import' | 'export' | 'detail' | 'edit' | 'recall' | 'other';
  readonly identifierKey: string | null;
}

export function parseProductsPathname(pathname: string): ParsedProductRoute {
  const normalized = pathname.replace(/\/+$/, '') || '/';
  if (!normalized.startsWith('/products')) {
    return { kind: 'other', identifierKey: null };
  }
  const suffix = normalized.slice('/products'.length);
  if (suffix === '' || suffix === '/') {
    return { kind: 'list', identifierKey: null };
  }
  const segments = suffix
    .split('/')
    .filter((s) => s.length > 0)
    .map((s) => {
      try {
        return decodeURIComponent(s);
      } catch {
        return s;
      }
    });

  const first = segments[0];
  const second = segments[1];
  if (!first) return { kind: 'list', identifierKey: null };
  if (first === 'new' && segments.length === 1) {
    return { kind: 'new', identifierKey: null };
  }
  if (first === 'import' && segments.length === 1) {
    return { kind: 'import', identifierKey: null };
  }
  if (first === 'export' && segments.length === 1) {
    return { kind: 'export', identifierKey: null };
  }
  if (second === 'edit') {
    return { kind: 'edit', identifierKey: first };
  }
  if (second === 'recall') {
    return { kind: 'recall', identifierKey: first };
  }
  return { kind: 'detail', identifierKey: first };
}

function RouterSyncActive(params: {
  readonly initialSelectedKey?: string | null | undefined;
  readonly initialEditMode?: boolean | undefined;
  readonly setSelectedKey: (key: string | null) => void;
  readonly setIsEditing: (editing: boolean) => void;
  readonly setShowRecallConfirm: (show: boolean) => void;
  readonly setShowNewForm: (show: boolean) => void;
  readonly setShowCsvImportPanel: (show: boolean) => void;
  readonly onExportCsv?: (() => void) | undefined;
  readonly onRegisterNavigate: (fn: (to: string) => void) => void;
}): null {
  const location = useLocation();
  const navigate = useNavigate();
  const lastPathnameRef = useRef<string | null>(null);
  const lastSearchRef = useRef<string | null>(null);

  const setSelectedKeyRef = useRef(params.setSelectedKey);
  const setIsEditingRef = useRef(params.setIsEditing);
  const setShowRecallConfirmRef = useRef(params.setShowRecallConfirm);
  const setShowNewFormRef = useRef(params.setShowNewForm);
  const setShowCsvImportPanelRef = useRef(params.setShowCsvImportPanel);
  const onExportCsvRef = useRef(params.onExportCsv);
  const onRegisterNavigateRef = useRef(params.onRegisterNavigate);

  setSelectedKeyRef.current = params.setSelectedKey;
  setIsEditingRef.current = params.setIsEditing;
  setShowRecallConfirmRef.current = params.setShowRecallConfirm;
  setShowNewFormRef.current = params.setShowNewForm;
  setShowCsvImportPanelRef.current = params.setShowCsvImportPanel;
  onExportCsvRef.current = params.onExportCsv;
  onRegisterNavigateRef.current = params.onRegisterNavigate;

  useEffect(() => {
    onRegisterNavigateRef.current((to: string) => {
      lastPathnameRef.current = to;
      navigate(to);
    });
  }, [navigate]);

  useEffect(() => {
    if (
      lastPathnameRef.current === location.pathname &&
      lastSearchRef.current === location.search
    ) {
      return;
    }
    const isInitialMount = lastPathnameRef.current === null;
    lastPathnameRef.current = location.pathname;
    lastSearchRef.current = location.search;

    const searchParams = new URLSearchParams(location.search);
    const selectParam = searchParams.get('select')?.trim();
    if (
      selectParam &&
      selectParam.length > 0 &&
      location.pathname.replace(/\/+$/, '') === '/products'
    ) {
      setSelectedKeyRef.current(selectParam);
      setShowNewFormRef.current(false);
      setShowCsvImportPanelRef.current(false);
      setIsEditingRef.current(false);
      setShowRecallConfirmRef.current(false);
      return;
    }

    const parsed = parseProductsPathname(location.pathname);
    if (parsed.kind === 'other') return;

    if (parsed.kind === 'list') {
      if (isInitialMount && params.initialSelectedKey) {
        return;
      }
      setSelectedKeyRef.current(null);
      setIsEditingRef.current(false);
      setShowRecallConfirmRef.current(false);
      setShowNewFormRef.current(false);
      setShowCsvImportPanelRef.current(false);
      return;
    }

    if (parsed.kind === 'new') {
      setSelectedKeyRef.current(null);
      setIsEditingRef.current(false);
      setShowRecallConfirmRef.current(false);
      setShowCsvImportPanelRef.current(false);
      setShowNewFormRef.current(true);
      return;
    }

    if (parsed.kind === 'import') {
      setSelectedKeyRef.current(null);
      setIsEditingRef.current(false);
      setShowRecallConfirmRef.current(false);
      setShowNewFormRef.current(false);
      setShowCsvImportPanelRef.current(true);
      return;
    }

    if (parsed.kind === 'export') {
      onExportCsvRef.current?.();
      lastPathnameRef.current = '/products';
      navigate('/products', { replace: true });
      return;
    }

    if (parsed.identifierKey !== null) {
      setSelectedKeyRef.current(parsed.identifierKey);
      setShowNewFormRef.current(false);
      setShowCsvImportPanelRef.current(false);
      setIsEditingRef.current(
        parsed.kind === 'edit' || (isInitialMount && Boolean(params.initialEditMode)),
      );
      setShowRecallConfirmRef.current(parsed.kind === 'recall');
    }
  }, [location.pathname, location.search, params.initialSelectedKey, params.initialEditMode]);

  return null;
}

export function useProductsRouterNavigation(params: {
  readonly initialSelectedKey?: string | null | undefined;
  readonly initialEditMode?: boolean | undefined;
  readonly selectedRecordKey?: string | undefined;
  readonly showRecallConfirm: boolean;
  readonly editingIndex: number | null;
  readonly isEditing: boolean;
  readonly showNewForm: boolean;
  readonly showCsvImportPanel: boolean;
  readonly setSelectedKey: (key: string | null) => void;
  readonly setIsEditing: (editing: boolean) => void;
  readonly setShowAdvanced: (show: boolean) => void;
  readonly setEditingIndex: (idx: number | null) => void;
  readonly setShowRecallConfirm: (show: boolean) => void;
  readonly setRecallError: (err: string | null) => void;
  readonly setShowNewForm: (show: boolean) => void;
  readonly setNewFormError: (err: string | null) => void;
  readonly setShowCsvImportPanel: (show: boolean) => void;
  readonly setCsvImportError: (err: string | null) => void;
  readonly setCsvImportSuccess: (msg: string | null) => void;
  readonly setLinkFormError: (err: string | null) => void;
  readonly setLinkFormSuccess: (msg: string | null) => void;
  readonly resetLinkForm: () => void;
  readonly handleOpenProduct: (key: string) => void;
  readonly handleStartEditingProduct: () => void;
  readonly handleCancelEditingProduct: () => void;
  readonly handleSaveProductEdit: (e: FormEvent) => Promise<boolean>;
  readonly onExportCsv?: (() => void) | undefined;
}) {
  const inRouter = useInRouterContext();
  const navigateFnRef = useRef<((to: string) => void) | null>(null);

  const pushProductPath = (path: string): void => {
    if (inRouter && navigateFnRef.current !== null) {
      navigateFnRef.current(path);
    }
  };

  const handleSetShowNewForm = (next: boolean): void => {
    params.setShowNewForm(next);
    pushProductPath(next ? '/products/new' : '/products');
  };

  const handleSetShowCsvImportPanel = (next: boolean): void => {
    params.setShowCsvImportPanel(next);
    pushProductPath(next ? '/products/import' : '/products');
  };

  const handleSetShowRecallConfirm = (next: boolean): void => {
    params.setShowRecallConfirm(next);
    if (params.selectedRecordKey) {
      const base = `/products/${encodeURIComponent(params.selectedRecordKey)}`;
      pushProductPath(next ? `${base}/recall` : base);
    }
  };

  const handleOpenProductWithRoute = (identifierKey: string): void => {
    params.handleOpenProduct(identifierKey);
    pushProductPath(`/products/${encodeURIComponent(identifierKey)}`);
  };

  const handleBackToProducts = (): void => {
    params.setSelectedKey(null);
    params.setIsEditing(false);
    params.setShowAdvanced(false);
    params.setEditingIndex(null);
    params.setLinkFormError(null);
    params.setLinkFormSuccess(null);
    pushProductPath('/products');
  };

  const handleStartEditingProductWithRoute = (): void => {
    params.handleStartEditingProduct();
    if (params.selectedRecordKey) {
      pushProductPath(`/products/${encodeURIComponent(params.selectedRecordKey)}/edit`);
    }
  };

  const handleCancelEditingProductWithRoute = (): void => {
    params.handleCancelEditingProduct();
    if (params.selectedRecordKey) {
      pushProductPath(`/products/${encodeURIComponent(params.selectedRecordKey)}`);
    }
  };

  const handleSaveProductEditWithRoute = async (e: FormEvent): Promise<void> => {
    const keyBeforeSave = params.selectedRecordKey;
    if ((await params.handleSaveProductEdit(e)) && keyBeforeSave) {
      pushProductPath(`/products/${encodeURIComponent(keyBeforeSave)}`);
    }
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return;
      if (params.showRecallConfirm) {
        handleSetShowRecallConfirm(false);
        params.setRecallError(null);
      } else if (params.editingIndex !== null) {
        params.resetLinkForm();
      } else if (params.isEditing) {
        handleCancelEditingProductWithRoute();
      } else if (params.showNewForm) {
        handleSetShowNewForm(false);
        params.setNewFormError(null);
      } else if (params.showCsvImportPanel) {
        handleSetShowCsvImportPanel(false);
        params.setCsvImportError(null);
        params.setCsvImportSuccess(null);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleNew = (): void => {
      handleSetShowNewForm(!params.showNewForm);
      params.setNewFormError(null);
    };
    const handleImport = (): void => {
      handleSetShowCsvImportPanel(!params.showCsvImportPanel);
      params.setCsvImportError(null);
      params.setCsvImportSuccess(null);
    };
    const handleExport = (): void => {
      params.onExportCsv?.();
    };
    window.addEventListener('mp:toggle-new-product', handleNew);
    window.addEventListener('mp:toggle-import-csv', handleImport);
    window.addEventListener('mp:export-csv', handleExport);
    return () => {
      window.removeEventListener('mp:toggle-new-product', handleNew);
      window.removeEventListener('mp:toggle-import-csv', handleImport);
      window.removeEventListener('mp:export-csv', handleExport);
    };
  });

  return {
    inRouter,
    pushProductPath,
    handleSetShowNewForm,
    handleSetShowCsvImportPanel,
    handleSetShowRecallConfirm,
    handleOpenProductWithRoute,
    handleBackToProducts,
    handleStartEditingProductWithRoute,
    handleCancelEditingProductWithRoute,
    handleSaveProductEditWithRoute,
    routerSyncElement: inRouter ? (
      <RouterSyncActive
        initialSelectedKey={params.initialSelectedKey}
        initialEditMode={params.initialEditMode}
        setSelectedKey={params.setSelectedKey}
        setIsEditing={params.setIsEditing}
        setShowRecallConfirm={params.setShowRecallConfirm}
        setShowNewForm={params.setShowNewForm}
        setShowCsvImportPanel={params.setShowCsvImportPanel}
        onExportCsv={params.onExportCsv}
        onRegisterNavigate={(fn) => {
          navigateFnRef.current = fn;
        }}
      />
    ) : null,
  };
}
